import { useLocalStorage } from "react-use";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import React from "react";
import { Item, FileItem, FolderItem } from "@/types/fse";

interface RecentFileItem {
  id: string;
  name: string;
  type: "file";
  url: string;
}

interface RecentFolderItem {
  id: string;
  name: string;
  type: "folder";
  url: string;
}

export const useRecentItems = (maxItems: number = 5) => {
  const userData = useAuthenticatedUser();

  const filesKey = `newRecentFiles_${userData.email}`;
  const foldersKey = `newRecentFolders_${userData.email}`;

  const [recentFiles, setRecentFiles] = useLocalStorage<RecentFileItem[]>(filesKey, []);
  const [recentFolders, setRecentFolders] = useLocalStorage<RecentFolderItem[]>(foldersKey, []);

  const addRecentFile = React.useCallback(
    (file: FileItem) => {
      const newItem: RecentFileItem = {
        id: file.id,
        name: file.name,
        type: "file",
        url: `/chat/${file.id}`,
      };

      setRecentFiles(prev => [newItem, ...(prev || []).filter(i => i.id !== file.id)].slice(0, maxItems));
    },
    [setRecentFiles, maxItems],
  );

  const addRecentFolder = React.useCallback(
    (folder: FolderItem) => {
      const newItem: RecentFolderItem = {
        id: folder.id,
        name: folder.name,
        type: "folder",
        url: `/drive/${folder.id}`,
      };

      setRecentFolders(prev => [newItem, ...(prev || []).filter(i => i.id !== folder.id)].slice(0, maxItems));
    },
    [setRecentFolders, maxItems],
  );

  const addRecentItem = React.useCallback(
    (item: Item) => {
      if (item.type === "file") {
        addRecentFile(item);
      } else if (item.type === "folder") {
        addRecentFolder(item);
      }
    },
    [addRecentFile, addRecentFolder],
  );

  return {
    recentFiles: recentFiles || [],
    recentFolders: recentFolders || [],
    addRecentFile,
    addRecentFolder,
    addRecentItem,
  };
};
